import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { myPagePath } from '../utils/myPage';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";
import BetaForm from '../BetaForm';
import { API } from '../utils/constants';

const TermsOfService = () => {
  const navigate = useNavigate();
  const [showSignUpDialog, setShowSignUpDialog] = useState(false);
  const [showBetaDialog, setShowBetaDialog] = useState(false);
  const [formData, setFormData] = useState({
    firstName: "",
    email: "",
    password: "",
    confirmPassword: "",
    accessCode: ""
  });

  const handleSignUp = async (e) => {
    e.preventDefault();
    
    if (!formData.firstName || !formData.email || !formData.password || !formData.confirmPassword) {
      toast.error("Please fill in all fields");
      return;
    }
    
    if (formData.password !== formData.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    
    try {
      const response = await axios.post(`${API}/register`, {
        email: formData.email,
        first_name: formData.firstName,
        password: formData.password,
        access_code: formData.accessCode
      });
      
      if (response.data.success) {
        localStorage.setItem('adminToken', response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        setShowSignUpDialog(false);
        navigate(myPagePath());
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Registration failed";
      toast.error(errorMsg);
    }
  };

  return (
    <div style={{ backgroundColor: '#EEE7D8', minHeight: '100vh' }}>
      {/* Header Navigation */}
      <div className="px-4 md:px-8 py-4 md:py-6 flex justify-between items-center relative z-10">
        <div style={{ cursor: 'pointer' }} onClick={() => navigate('/')}>
          <img 
            src="https://customer-assets.emergentagent.com/job_recipehub-15/artifacts/wndzktxu_wellby-logo.svg"
            alt="Wellby"
            style={{ 
              height: '60px',
              width: 'auto'
            }}
          />
        </div>
        <div className="flex gap-3">
          <Button 
            onClick={() => setShowSignUpDialog(true)} 
            variant="outline"
            className="px-4 py-2 md:px-6 md:py-3 font-medium text-sm md:text-base hover-blush transition-colors focus:outline-none focus:ring-0"
            style={{ 
              borderColor: '#51120A',
              color: '#51120A',
              fontFamily: "'Bricolage Grotesque', sans-serif",
              borderWidth: '1px'
            }}
          >
            Sign Up
          </Button>
          <Button 
            onClick={() => setShowBetaDialog(true)} 
            className="px-4 py-2 md:px-6 md:py-3 text-white font-medium text-sm md:text-base"
            style={{ 
              backgroundColor: '#51120A',
              borderColor: '#51120A',
              fontFamily: "'Bricolage Grotesque', sans-serif"
            }}
          >
            Join Beta
          </Button>
        </div>
      </div>

      <div style={{ maxWidth: '800px', margin: '0 auto', padding: '60px 20px' }}>

        {/* Content */}
        <div style={{ fontFamily: "'Bricolage Grotesque', sans-serif", color: '#51120A' }}>
          <h1 style={{ fontFamily: "'Instrument Serif', serif", fontSize: '48px', marginBottom: '16px' }}>
            Terms of Service
          </h1>
          
          <p style={{ fontSize: '16px', marginBottom: '40px', opacity: 0.7 }}>
            Last updated: December 14, 2025
          </p>

          <p style={{ fontSize: '16px', lineHeight: '1.8', marginBottom: '32px' }}>
            Welcome to Wellby. These Terms of Service ("Terms") govern your access to and use of the Wellby website, application, and related services (collectively, the "Service").
          </p>

          <p style={{ fontSize: '16px', lineHeight: '1.8', marginBottom: '40px' }}>
            By using Wellby, you agree to these Terms. If you do not agree, please do not use the Service.
          </p>

          {/* About Wellby */}
          <h2 style={{ fontFamily: "'Instrument Serif', serif", fontSize: '32px', marginBottom: '16px', marginTop: '40px' }}>
            About Wellby
          </h2>
          
          <p style={{ fontSize: '16px', lineHeight: '1.8', marginBottom: '16px' }}>
            Wellby is a digital tool for saving, organizing, and sharing personal recipes. Some content on Wellby may be private, and some may be shared publicly at your discretion.
          </p>

          <p style={{ fontSize: '16px', lineHeight: '1.8', marginBottom: '40px' }}>
            Wellby is currently in beta, which means features may change, improve, or occasionally break. We appreciate your patience as we build.
          </p>

          {/* Eligibility */}
          <h2 style={{ fontFamily: "'Instrument Serif', serif", fontSize: '32px', marginBottom: '16px', marginTop: '40px' }}>
            Eligibility
          </h2>

          <p style={{ fontSize: '16px', lineHeight: '1.8', marginBottom: '40px' }}>
            You must be at least 13 years old to use Wellby. By using the Service, you confirm that you meet this requirement.
          </p>

          {/* Your Account */}
          <h2 style={{ fontFamily: "'Instrument Serif', serif", fontSize: '32px', marginBottom: '16px', marginTop: '40px' }}>
            Your Account
          </h2>

          <p style={{ fontSize: '16px', lineHeight: '1.8', marginBottom: '16px' }}>
            When you create an account with Wellby:
          </p>

          <ul style={{ fontSize: '16px', lineHeight: '1.8', marginBottom: '24px', paddingLeft: '24px' }}>
            <li style={{ marginBottom: '12px' }}>You are responsible for maintaining the security of your account</li>
            <li style={{ marginBottom: '12px' }}>You agree to provide accurate information</li>
            <li style={{ marginBottom: '12px' }}>You are responsible for all activity that occurs under your account</li>
            <li style={{ marginBottom: '12px' }}>You may stop using Wellby at any time.</li>
          </ul>

          {/* Your Content */}
          <h2 style={{ fontFamily: "'Instrument Serif', serif", fontSize: '32px', marginBottom: '16px', marginTop: '40px' }}>
            Your Content
          </h2>

          <p style={{ fontSize: '16px', lineHeight: '1.8', marginBottom: '16px' }}>
            You retain ownership of all recipes and content you create or upload to Wellby.
          </p>

          <p style={{ fontSize: '16px', lineHeight: '1.8', marginBottom: '16px' }}>
            By using the Service, you grant Wellby a limited license to store, display, and share your content only as needed to operate the Service, including:
          </p>

          <ul style={{ fontSize: '16px', lineHeight: '1.8', marginBottom: '16px', paddingLeft: '24px' }}>
            <li style={{ marginBottom: '12px' }}>Displaying your recipes in your account</li>
            <li style={{ marginBottom: '12px' }}>Showing content on public pages when you choose to make it public</li>
            <li style={{ marginBottom: '12px' }}>Enabling sharing via links</li>
          </ul>

          <p style={{ fontSize: '16px', lineHeight: '1.8', marginBottom: '40px', fontWeight: '600' }}>
            We will never sell your recipes or personal content.
          </p>

          {/* Public Pages & Sharing */}
          <h2 style={{ fontFamily: "'Instrument Serif', serif", fontSize: '32px', marginBottom: '16px', marginTop: '40px' }}>
            Public Pages & Sharing
          </h2>

          <p style={{ fontSize: '16px', lineHeight: '1.8', marginBottom: '16px' }}>
            If you choose to make a recipe or collection public:
          </p>

          <ul style={{ fontSize: '16px', lineHeight: '1.8', marginBottom: '40px', paddingLeft: '24px' }}>
            <li style={{ marginBottom: '12px' }}>Anyone with the link may be able to view it</li>
            <li style={{ marginBottom: '12px' }}>Wellby is not responsible for how others use or share publicly available content</li>
            <li style={{ marginBottom: '12px' }}>You control what is public and can change this at any time.</li>
          </ul>

          {/* Acceptable Use */}
          <h2 style={{ fontFamily: "'Instrument Serif', serif", fontSize: '32px', marginBottom: '16px', marginTop: '40px' }}>
            Acceptable Use
          </h2>

          <p style={{ fontSize: '16px', lineHeight: '1.8', marginBottom: '16px' }}>
            You agree not to:
          </p>

          <ul style={{ fontSize: '16px', lineHeight: '1.8', marginBottom: '16px', paddingLeft: '24px' }}>
            <li style={{ marginBottom: '12px' }}>Use Wellby for unlawful purposes</li>
            <li style={{ marginBottom: '12px' }}>Upload content that is harmful, abusive, or violates others' rights</li>
            <li style={{ marginBottom: '12px' }}>Attempt to disrupt or misuse the Service</li>
            <li style={{ marginBottom: '12px' }}>Impersonate others or misrepresent your identity</li>
          </ul>

          <p style={{ fontSize: '16px', lineHeight: '1.8', marginBottom: '40px' }}>
            We reserve the right to remove content or suspend accounts that violate these Terms.
          </p>

          {/* Beta Disclaimer */}
          <h2 style={{ fontFamily: "'Instrument Serif', serif", fontSize: '32px', marginBottom: '16px', marginTop: '40px' }}>
            Beta Disclaimer
          </h2>

          <p style={{ fontSize: '16px', lineHeight: '1.8', marginBottom: '16px' }}>
            Wellby is provided on an "as is" and "as available" basis.
          </p>

          <p style={{ fontSize: '16px', lineHeight: '1.8', marginBottom: '16px' }}>
            Because this is a beta product:
          </p>

          <ul style={{ fontSize: '16px', lineHeight: '1.8', marginBottom: '16px', paddingLeft: '24px' }}>
            <li style={{ marginBottom: '12px' }}>Features may change or be discontinued</li>
            <li style={{ marginBottom: '12px' }}>Data loss is unlikely but possible</li>
            <li style={{ marginBottom: '12px' }}>The Service may experience downtime or bugs</li>
          </ul>

          <p style={{ fontSize: '16px', lineHeight: '1.8', marginBottom: '40px' }}>
            We recommend keeping backups of important recipes.
          </p>

          {/* No Professional Advice */}
          <h2 style={{ fontFamily: "'Instrument Serif', serif", fontSize: '32px', marginBottom: '16px', marginTop: '40px' }}>
            No Professional Advice
          </h2>

          <p style={{ fontSize: '16px', lineHeight: '1.8', marginBottom: '40px' }}>
            Recipes and content on Wellby are provided by users. Wellby does not offer medical, nutritional, or dietary advice. You are responsible for how you use any recipe or information.
          </p>

          {/* Termination */}
          <h2 style={{ fontFamily: "'Instrument Serif', serif", fontSize: '32px', marginBottom: '16px', marginTop: '40px' }}>
            Termination
          </h2>

          <p style={{ fontSize: '16px', lineHeight: '1.8', marginBottom: '16px' }}>
            We may suspend or terminate access to Wellby if:
          </p>

          <ul style={{ fontSize: '16px', lineHeight: '1.8', marginBottom: '16px', paddingLeft: '24px' }}>
            <li style={{ marginBottom: '12px' }}>These Terms are violated</li>
            <li style={{ marginBottom: '12px' }}>The Service is discontinued</li>
            <li style={{ marginBottom: '12px' }}>Required by law</li>
          </ul>

          <p style={{ fontSize: '16px', lineHeight: '1.8', marginBottom: '40px' }}>
            You may request account deletion at any time.
          </p>

          {/* Limitation of Liability */}
          <h2 style={{ fontFamily: "'Instrument Serif', serif", fontSize: '32px', marginBottom: '16px', marginTop: '40px' }}>
            Limitation of Liability
          </h2>

          <p style={{ fontSize: '16px', lineHeight: '1.8', marginBottom: '16px' }}>
            To the fullest extent permitted by law:
          </p>

          <ul style={{ fontSize: '16px', lineHeight: '1.8', marginBottom: '40px', paddingLeft: '24px' }}>
            <li style={{ marginBottom: '12px' }}>Wellby is not liable for indirect, incidental, or consequential damages</li>
            <li style={{ marginBottom: '12px' }}>Use of the Service is at your own risk</li>
          </ul>

          {/* Changes to These Terms */}
          <h2 style={{ fontFamily: "'Instrument Serif', serif", fontSize: '32px', marginBottom: '16px', marginTop: '40px' }}>
            Changes to These Terms
          </h2>

          <p style={{ fontSize: '16px', lineHeight: '1.8', marginBottom: '40px' }}>
            We may update these Terms from time to time. If we do, we'll update the "Last updated" date. Continued use of Wellby means you accept the updated Terms.
          </p>

          {/* Contact */}
          <h2 style={{ fontFamily: "'Instrument Serif', serif", fontSize: '32px', marginBottom: '16px', marginTop: '40px' }}>
            Contact
          </h2>

          <p style={{ fontSize: '16px', lineHeight: '1.8', marginBottom: '8px' }}>
            If you have questions about these Terms, please contact:
          </p>

          <p style={{ fontSize: '16px', lineHeight: '1.8', marginBottom: '8px' }}>
            <strong>Wellby</strong>
          </p>

          <p style={{ fontSize: '16px', lineHeight: '1.8', marginBottom: '60px' }}>
            Email: <a href="mailto:britt@getwellby.com" style={{ color: '#875C66', textDecoration: 'underline' }}>britt@getwellby.com</a>
          </p>
        </div>
      </div>

      {/* Sign Up Dialog */}
      <Dialog open={showSignUpDialog} onOpenChange={setShowSignUpDialog}>
        <DialogContent style={{ backgroundColor: '#F3EEE4', border: '1px solid #D6D0C2', boxShadow: 'none' }}>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Instrument Serif', serif", color: '#52120A' }}>Sign Up</DialogTitle>
            <DialogDescription style={{ fontFamily: "'Bricolage Grotesque', sans-serif", color: '#52120A' }}>Create your account to access the recipe manager</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSignUp} className="space-y-4">
            <div>
              <Label htmlFor="accessCode" style={{ fontFamily: "'Bricolage Grotesque', sans-serif", color: '#52120A' }}>Invite Code</Label>
              <Input
                id="accessCode"
                type="text"
                value={formData.accessCode || ''}
                onChange={(e) => setFormData({ ...formData, accessCode: e.target.value })}
                required
                placeholder="Enter your invite code"
                style={{ borderColor: '#52120A', fontFamily: "'Bricolage Grotesque', sans-serif" }}
              />
            </div>
            <div>
              <Label htmlFor="firstName" style={{ fontFamily: "'Bricolage Grotesque', sans-serif", color: '#52120A' }}>First Name</Label>
              <Input
                id="firstName"
                type="text"
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                required
                style={{ borderColor: '#52120A', fontFamily: "'Bricolage Grotesque', sans-serif" }}
              />
            </div>
            <div>
              <Label htmlFor="email" style={{ fontFamily: "'Bricolage Grotesque', sans-serif", color: '#52120A' }}>Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                style={{ borderColor: '#52120A', fontFamily: "'Bricolage Grotesque', sans-serif" }}
              />
            </div>
            <div>
              <Label htmlFor="password" style={{ fontFamily: "'Bricolage Grotesque', sans-serif", color: '#52120A' }}>Password</Label>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                style={{ borderColor: '#52120A', fontFamily: "'Bricolage Grotesque', sans-serif" }}
              />
            </div>
            <div>
              <Label htmlFor="confirmPassword" style={{ fontFamily: "'Bricolage Grotesque', sans-serif", color: '#52120A' }}>Confirm Password</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                required
                style={{ borderColor: '#52120A', fontFamily: "'Bricolage Grotesque', sans-serif" }}
              />
            </div>
            <Button type="submit" className="w-full" style={{ backgroundColor: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif" }}>
              Sign Up
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Beta Form Dialog */}
      <BetaForm open={showBetaDialog} onOpenChange={setShowBetaDialog} />
    </div>
  );
};

export default TermsOfService;
